import React, {ReactNode} from 'react'
import {currentMedia, HideInPC, HideInSP} from '../../styledComponents/ResponsiveConvenience'
import PPStepper from '../shared/PPStepper'
import styled from 'styled-components'
import {StyledPageTitle} from '../reservations/components/CommonStyle'
import {DisplayItemDividerForPageTop, DisplayItemTitle} from '../../styledComponents/DisplayItem'
import {PlainStyledButton, StyledSecondaryButton} from '../../styledComponents/Button'
import {Colors, FontSize, FontWeights} from '../../styledComponents/Constants'
import {faPenToSquare} from '@fortawesome/free-regular-svg-icons/faPenToSquare'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {CenterRowFlexContainer} from '../../styledComponents/FlexComponents'
import {QRCodeScannerWithGate} from '../shared/qrCodeScanner/QRCodeScannerWithGate'
import MessageDialogComponent from '../../common/resident/MessageDialogComponent'
import {Nullable} from '../../app/TypeHelpers'
import ResidentGeneralMeetingRepo, {CheckUnitOwnerConfirmationKeyResponse} from './ResidentGeneralMeetingRepo'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet
} from '../shared/hoc/createResidentWithErrorHandlingPage'
import {Messages} from '../../Messages'
import {PlainBackLink} from '../../styledComponents/Links'
import {Location} from 'react-router-dom'
import {QrCodeScanner} from '@mui/icons-material'
import {Box} from '@mui/material'

export const unitOwnerConfirmationStepsForPc: string[] = [
    '確認方法の選択',
    '区分所有者の確認',
    '確認完了'
]

export const unitOwnerConfirmationStepsForSp: string[] = [
    '方法選択',
    '確認',
    '確認完了'
]

export interface ResidentUnitOwnerConfirmationVerificationMethodSelectionLocationState {
    rootPageUrl: string
}

interface Props {
    location: Location<ResidentUnitOwnerConfirmationVerificationMethodSelectionLocationState>
    residentGeneralMeetingRepo: ResidentGeneralMeetingRepo
}

interface State {
    isOpenQRCodeScanner: boolean
    isPauseQRCodeScanner: boolean
    isOpenDeniedCameraUseDialog: boolean
    isOpenIncorrectQRCodeDialog: boolean
}

class ResidentUnitOwnerConfirmationVerificationMethodSelectionPage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {
    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)
        this.state = {
            isOpenQRCodeScanner: false,
            isPauseQRCodeScanner: false,
            isOpenDeniedCameraUseDialog: false,
            isOpenIncorrectQRCodeDialog: false,
        }
        this.handle2DCodeScanButtonClick = this.handle2DCodeScanButtonClick.bind(this)
        this.handleConfirmationKeyInputButtonClick = this.handleConfirmationKeyInputButtonClick.bind(this)
        this.handleBackButtonClick = this.handleBackButtonClick.bind(this)
        this.handleQRCodeScannerScan = this.handleQRCodeScannerScan.bind(this)
        this.handleQRCodeScannerClose = this.handleQRCodeScannerClose.bind(this)
    }

    handle2DCodeScanButtonClick = () => {
        this.setState({
            isOpenQRCodeScanner: true,
        })
    }

    handleConfirmationKeyInputButtonClick = () => {
        this.props.navigate(
            '/resident/general-meeting/unit-owner-confirmation-key-input',
            {
                state: {
                    rootPageUrl: this.props.location.state.rootPageUrl,
                },
            }
        )
    }

    handleBackButtonClick = () => {
        this.props.navigate(this.props.location.state.rootPageUrl)
    }

    handleQRCodeScannerScan = async (text: string) => {
        await this.props.eventEmitter.showLoading()
        try {
            this.setState({
                isPauseQRCodeScanner: true,
            })
            const path = text.match('/resident/login\\?seniurl=/resident/general-meeting/unit-owner-confirmation-redirect\\?unitOwnerConfirmationKey=.*')

            if (!path) {
                this.setState({
                    isOpenIncorrectQRCodeDialog: true,
                })
                return
            }

            const result = path[0].match('unitOwnerConfirmationKey=(.*)')
            let unitOwnerConfirmationKey = ''
            if (result) {
                unitOwnerConfirmationKey = result[1]
            }

            const response: Nullable<CheckUnitOwnerConfirmationKeyResponse> = await this.props.asyncCallFunction(
                this.props.residentGeneralMeetingRepo.checkUnitOwnerConfirmationKey.bind(
                    this.props.residentGeneralMeetingRepo,
                    unitOwnerConfirmationKey,
                ),
                async () => {
                    this.props.navigate('/resident/general-meeting/error')
                },
            )

            if (!response || response.requestError !== null) {
                return
            }

            this.props.navigate(
                '/resident/general-meeting/unit-owner-confirmation',
                {
                    state: {
                        unitOwnerConfirmationKey: unitOwnerConfirmationKey,
                    },
                },
            )
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    handleQRCodeScannerClose = () => {
        this.setState({
            isOpenQRCodeScanner: false,
        })
    }

    handleQRCodeScannerError = () => {
        this.setState({
            isOpenDeniedCameraUseDialog: true,
        })
    }

    handleDeniedCameraUseDialogOKButtonClick = () => {
        this.setState({
            isOpenDeniedCameraUseDialog: false,
            isOpenQRCodeScanner: false,
        })
    }

    handleIncorrectQRCodeDialogOKButtonClick = () => {
        this.setState({
            isOpenIncorrectQRCodeDialog: false,
        })
        setTimeout(() => {
            this.setState({
                isPauseQRCodeScanner: false,
            })
        }, 1000)
    }

    render(): ReactNode {
        return <>
            <ContentArea>
                <PlainBackLink
                    to={this.props.location.state.rootPageUrl}>
                    戻る
                </PlainBackLink>
                <StyledDisplayItemDividerForPageTop/>
                <HideInSP>
                    <PPStepper steps={unitOwnerConfirmationStepsForPc} activeStep={0}/>
                </HideInSP>
                <HideInPC>
                    <PPStepper steps={unitOwnerConfirmationStepsForSp} activeStep={0} alternativeLabel/>
                </HideInPC>
                <UnitOwnerStyledPageTitle>確認方法の選択</UnitOwnerStyledPageTitle>
                <DetailMessage>
                    あなたが区分所有者ご本人であることを確認します。別途配布の「区分所有者確認の方法」をお手元にご用意ください。
                    <StyledUl>
                        <li>この確認は一度行うと次回の総会以降は必要ありません。</li>
                    </StyledUl>
                </DetailMessage>
                <StyledDisplayItemTitle>区分所有者確認</StyledDisplayItemTitle>
                <VerificationMethodButtonFlexContainer>
                    <StyledButton
                        onClick={this.handle2DCodeScanButtonClick}
                    >
                        <StyledQrCodeScannerIcon/>
                        <ButtonText>確認用二次元コードを読み取る</ButtonText>
                    </StyledButton>
                    <StyledButton
                        onClick={this.handleConfirmationKeyInputButtonClick}
                    >
                        <FontAwesomeIcon icon={faPenToSquare} color={Colors.Primary} fontSize={'38px'}/>
                        <ButtonText>
                            確認用キーを入力する
                        </ButtonText>
                    </StyledButton>
                </VerificationMethodButtonFlexContainer>
                <BackButton
                    onClick={this.handleBackButtonClick}
                >
                    戻る
                </BackButton>
                <Box height={'15px'}/>
            </ContentArea>
            {this.state.isOpenQRCodeScanner &&
                <QRCodeScannerWithGate
                    descriptions={[
                        '二次元コードを読み取って',
                        '区分所有者の確認へ進んでください',
                    ]}
                    pause={this.state.isPauseQRCodeScanner}
                    onScan={this.handleQRCodeScannerScan}
                    onClose={this.handleQRCodeScannerClose}
                    onError={this.handleQRCodeScannerError}
                />
            }
            <MessageDialogComponent
                open={this.state.isOpenDeniedCameraUseDialog}
                params={{
                    title: '確認',
                    errorLevel: Messages.WGMTR0003.errorLevel,
                    message: Messages.WGMTR0003.toString(),
                    buttonProps: [
                        {
                            isDefault: false,
                            buttonLabel: 'OK',
                            buttonHandler: this.handleDeniedCameraUseDialogOKButtonClick,
                        },
                    ],
                }}
            />
            <MessageDialogComponent
                open={this.state.isOpenIncorrectQRCodeDialog}
                params={{
                    title: '確認',
                    errorLevel: Messages.WGMTR0004.errorLevel,
                    message: Messages.WGMTR0004.toString(),
                    buttonProps: [
                        {
                            isDefault: false,
                            buttonLabel: 'OK',
                            buttonHandler: this.handleIncorrectQRCodeDialogOKButtonClick,
                        },
                    ],
                }}
            />
        </>
    }
}

export default createResidentWithErrorHandlingPage(ResidentUnitOwnerConfirmationVerificationMethodSelectionPage)

const ContentArea = styled.div`
  padding: 0 0 15px 0;
`

const StyledDisplayItemDividerForPageTop = styled(DisplayItemDividerForPageTop)`
  margin-top: 14px;
  margin-bottom: 15px;
`

const UnitOwnerStyledPageTitle = styled(StyledPageTitle)`
  margin-top: 30px;
`

const DetailMessage = styled.div`
  margin-top: 20px;
`

const StyledDisplayItemTitle = styled(DisplayItemTitle)`
  margin-top: 20px;
`

const StyledUl = styled.ul`
  margin: 0;
  padding-left: 25px;
`

const VerificationMethodButtonFlexContainer = styled(CenterRowFlexContainer)`
  margin-top: 20px;
  column-gap: 30px;
  row-gap: 20px;
  ${currentMedia.lessThan('sp')`
    flex-direction: column;
    width: 100%
  `}
`

const StyledButton = styled(PlainStyledButton)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: 100px;
  width: 354px;
  padding-left: 24px;

  font-size: ${FontSize.Body};
  font-weight: ${FontWeights.Bold};
  border: none;
  background: ${Colors.Surface};
  box-shadow: 0 2px 2px 0 rgba(112, 112, 112, 0.4);
  border-radius: 8px;
  color: ${Colors.Body};

  :active {
    background: linear-gradient(0deg, rgba(112, 112, 112, 0.75), rgba(112, 112, 112, 0.75)), #D9D9D9;
    box-shadow: none;
  }

  :disabled {
    pointer-events: none;
    background-color: ${Colors.AccentLight};
  }
`

const StyledQrCodeScannerIcon = styled(QrCodeScanner)`
  color: ${Colors.Primary};
  font-size: 38px;
`


const ButtonText = styled.div`
  margin-left: 20px;
`

const BackButton = styled(StyledSecondaryButton)`
  margin-top: 40px;
`
